import type {
  CollectInputField,
  TemplateSelection,
  TemplateSelectionCondition,
  TemplateSelectionConditionGroup,
} from "@common/schemas/icertis"

export type AnswerLabelsByFieldId = ReadonlyMap<string, string[]>

type RuleOutcome = "holds" | "fails" | "undecided"

const BOOLEAN_ANSWER_VALUES: Record<string, string> = { Yes: "true", No: "false" }

function sameValue(answerLabel: string, ruleValue: string): boolean {
  if (answerLabel === ruleValue) return true
  const booleanValue = BOOLEAN_ANSWER_VALUES[answerLabel]
  return booleanValue !== undefined && booleanValue === ruleValue.toLowerCase()
}

function outcomeOfCondition(condition: TemplateSelectionCondition, answers: AnswerLabelsByFieldId): RuleOutcome {
  const answerLabels = answers.get(condition.field_id)
  if (!answerLabels) return "undecided"
  const ruleValue = condition.value
  if (condition.operator === "hasavalue") return "holds"
  if (condition.operator === "hasnovalue") return "fails"
  if (ruleValue === null || ruleValue === undefined) return "undecided"
  const matchesAny = (matches: (answerLabel: string) => boolean): RuleOutcome =>
    answerLabels.some(matches) ? "holds" : "fails"
  const matchesNone = (matches: (answerLabel: string) => boolean): RuleOutcome =>
    answerLabels.some(matches) ? "fails" : "holds"
  const answersAreBoolean = answerLabels.every((answerLabel) => answerLabel in BOOLEAN_ANSWER_VALUES)
  switch (condition.operator) {
    case "eq":
      return matchesAny((answerLabel) => sameValue(answerLabel, ruleValue))
    case "is":
      return answersAreBoolean ? matchesAny((answerLabel) => sameValue(answerLabel, ruleValue)) : "fails"
    case "neq":
      return matchesNone((answerLabel) => sameValue(answerLabel, ruleValue))
    case "isnot":
      return answersAreBoolean ? matchesNone((answerLabel) => sameValue(answerLabel, ruleValue)) : "undecided"
    case "contains":
      return matchesAny((answerLabel) => answerLabel.includes(ruleValue))
    case "notcontains":
      return matchesNone((answerLabel) => answerLabel.includes(ruleValue))
    case "startswith":
      return matchesAny((answerLabel) => answerLabel.startsWith(ruleValue))
    case "notstartswith":
      return matchesNone((answerLabel) => answerLabel.startsWith(ruleValue))
    default:
      return "undecided"
  }
}

function combinedOutcome(operator: "and" | "or", outcomes: RuleOutcome[]): RuleOutcome {
  if (outcomes.length === 0) return "holds"
  const decidingOutcome: RuleOutcome = operator === "and" ? "fails" : "holds"
  const settledOutcome: RuleOutcome = operator === "and" ? "holds" : "fails"
  if (outcomes.includes(decidingOutcome)) return decidingOutcome
  return outcomes.every((outcome) => outcome === settledOutcome) ? settledOutcome : "undecided"
}

function outcomeOfGroup(group: TemplateSelectionConditionGroup, answers: AnswerLabelsByFieldId): RuleOutcome {
  return combinedOutcome(group.operator, [
    ...group.conditions.map((condition) => outcomeOfCondition(condition, answers)),
    ...group.groups.map((nested) => outcomeOfGroup(nested, answers)),
  ])
}

export function templatesStillValid(selection: TemplateSelection, answers: AnswerLabelsByFieldId): string[] {
  const templateNames = new Set<string>()
  for (const rule of selection.rules) {
    if (outcomeOfGroup(rule.when, answers) !== "fails") {
      for (const templateName of rule.templates) templateNames.add(templateName)
    }
  }
  return Array.from(templateNames).sort((first, second) => first.localeCompare(second))
}

function conditionsIn(group: TemplateSelectionConditionGroup): TemplateSelectionCondition[] {
  return [...group.conditions, ...group.groups.flatMap(conditionsIn)]
}

function readsField(rule: TemplateSelection["rules"][number], fieldIds: ReadonlySet<string>): boolean {
  return conditionsIn(rule.when).some((condition) => fieldIds.has(condition.field_id))
}

function templatesStillValidGiven(
  selection: TemplateSelection,
  answeredFields: CollectInputField[],
  answerLabelsFor: (fieldId: string) => string[],
): string[] {
  const answers = new Map<string, string[]>()
  for (const field of answeredFields) {
    const labels = answerLabelsFor(field.id)
    if (labels.length > 0) answers.set(field.id, labels)
  }
  return templatesStillValid(selection, answers)
}

export function templatesStillValidAboveField(
  selection: TemplateSelection,
  shownFields: CollectInputField[],
  fieldId: string,
  answerLabelsFor: (fieldId: string) => string[],
): string[] {
  const fieldIndex = shownFields.findIndex((field) => field.id === fieldId)
  return templatesStillValidGiven(selection, fieldIndex < 0 ? shownFields : shownFields.slice(0, fieldIndex), answerLabelsFor)
}

export type TemplateAvailability = "allowed" | "always"

export interface AvailableTemplate {
  name: string
  availability: TemplateAvailability
}

export function availableTemplatesAboveField(
  selection: TemplateSelection,
  fields: CollectInputField[],
  shownFields: CollectInputField[],
  fieldId: string,
  answerLabelsFor: (fieldId: string) => string[],
): AvailableTemplate[] {
  const questionIds = new Set(fields.filter((field) => field.id !== fieldId).map((field) => field.id))
  return templatesStillValidAboveField(selection, shownFields, fieldId, answerLabelsFor).map((name) => ({
    name,
    availability: selection.rules.some((rule) => rule.templates.includes(name) && !readsField(rule, questionIds))
      ? "always"
      : "allowed",
  }))
}
